import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Monitor, Puzzle, Smartphone } from 'lucide-react';
import { ConfirmModal } from './ui/Modal';
import { Section } from './settingsPrimitives';
import { useAuthStore } from '../store/authStore';
import { CONSENT_CHANGED_EVENT, choiceOf, readConsent } from '../services/vaultCloudConsent';
import { listDevices, revokeDevice } from '../services/vaultDevices';
import './VaultDevices.css';

const ICONS = { desktop: Monitor, extension: Puzzle, android: Smartphone, web: Globe };
const KIND_LABEL = { desktop: 'Desktop app', extension: 'Browser extension', android: 'Android', web: 'Web vault' };

function ago(iso, lang) {
  if (!iso) return null;
  const seconds = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(lang || 'en', { numeric: 'auto' });
  const abs = Math.abs(seconds);
  if (abs < 60) return rtf.format(0, 'second');
  if (abs < 3600) return rtf.format(Math.round(seconds / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(seconds / 3600), 'hour');
  return rtf.format(Math.round(seconds / 86400), 'day');
}

/**
 * Settings → Extensions → Vault → "Devices" (Standalone vault cards X1 / 6.2):
 * every device that gets this vault from the cloud — desktop, browser
 * extension, Android, web — with when it was last seen, and Revoke. A revoke
 * stops new downloads to that device; what it already holds stays there until
 * the master passphrase changes (said in the confirm). Shown while the vault
 * is stored in the cloud.
 */
export default function VaultDevicesSection() {
  const { t, i18n } = useTranslation();
  const signedIn = useAuthStore((s) => s.isAuthenticated);
  const [cloud, setCloud] = useState(false);
  const [devices, setDevices] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const read = useCallback(async () => {
    try {
      const consent = await readConsent();
      const on = choiceOf(consent) === 'cloud';
      setCloud(on);
      setDevices(on ? await listDevices() : null);
    } catch {
      setDevices(null);
    }
  }, []);

  useEffect(() => {
    if (!signedIn) return undefined;
    read();
    window.addEventListener(CONSENT_CHANGED_EVENT, read);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, read);
  }, [signedIn, read]);

  if (!signedIn || !cloud || !devices) return null;

  const revoke = async (device) => {
    setBusy(true);
    setError('');
    try {
      await revokeDevice(device.id);
      await read();
    } catch (e) {
      const msg = String(e?.message || '');
      setError(
        msg === 'reauth_cancelled' || msg === 'account_changed'
          ? t('vault.devicesReauth', 'Sign in again to confirm it is you. Nothing was changed.')
          : t('vault.devicesRevokeFailed', 'That did not work. Check your connection and try again.')
      );
    }
    setBusy(false);
  };

  return (
    <Section title={t('vault.devicesHeading', 'Devices')}>
      <div className="vault-devices" data-testid="vault-devices">
        {devices.length === 0 && (
          <div className="vault-devices-empty">{t('vault.devicesNone', 'No device has used your cloud vault yet.')}</div>
        )}
        {devices.map((d) => {
          const Icon = ICONS[d.kind] || Monitor;
          const revoked = !!d.revoked_at;
          return (
            <div key={d.id} className={`vault-device${revoked ? ' is-revoked' : ''}`} data-testid="vault-device">
              <Icon size={16} className="vault-device-icon" />
              <div className="vault-device-texts">
                <span className="vault-device-name">
                  {d.label || t(`vault.deviceKind.${d.kind}`, KIND_LABEL[d.kind] || d.kind)}
                  {d.current && <span className="vault-device-tag">{t('vault.deviceThis', 'This device')}</span>}
                </span>
                <span className="vault-device-meta">
                  {t(`vault.deviceKind.${d.kind}`, KIND_LABEL[d.kind] || d.kind)}
                  {' · '}
                  {revoked
                    ? t('vault.deviceRevoked', 'Removed {{when}}', { when: ago(d.revoked_at, i18n.language) })
                    : d.last_seen_at
                      ? t('vault.deviceSeen', 'Last seen {{when}}', { when: ago(d.last_seen_at, i18n.language) })
                      : t('vault.deviceNeverSeen', 'Not seen yet')}
                </span>
              </div>
              {!revoked && !d.current && (
                <button type="button" className="settings-action-btn" disabled={busy} onClick={() => setConfirm(d)}>
                  {t('vault.deviceRevoke', 'Remove')}
                </button>
              )}
            </div>
          );
        })}
        {error && <div className="vault-modal-error">{error}</div>}
        <ConfirmModal
          isOpen={!!confirm}
          onClose={() => setConfirm(null)}
          onConfirm={() => {
            const d = confirm;
            setConfirm(null);
            revoke(d);
          }}
          title={t('vault.deviceRevokeTitle', 'Remove this device from your vault?')}
          message={t(
            'vault.deviceRevokeBody',
            'It stops getting your vault from the cloud right away. What it already downloaded stays on it until you change your master passphrase — do that too if the device is lost.'
          )}
          confirmText={t('vault.deviceRevoke', 'Remove')}
          type="danger"
        />
      </div>
    </Section>
  );
}
